import { useState } from "react";
import { Play, X, ListMusic, ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { upNextStore, useUpNext, type QueueItem } from "@/lib/upNextStore";
import { playerStore } from "@/lib/playerStore";

/**
 * Renders the Up Next queue inside the expanded MiniPlayerBar panel.
 *
 * UX: collapsed by default — the expanded player surfaces transport controls
 * first (the most common action). A single on-brand "Up Next · N" toggle
 * reveals the queue as a secondary expansion. When open, items can be
 * reordered via drag-and-drop.
 */
export default function UpNextPanel() {
  const items = useUpNext((s) => s.items);
  const loading = useUpNext((s) => s.loading);
  const [open, setOpen] = useState(false);

  const count = items.length;
  const hasItems = count > 0;

  return (
    <div className="border-t-2 border-foreground/20 pt-3 mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="up-next-list"
        title={open ? "Hide Up Next" : "Show Up Next queue"}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 border-2 border-foreground rounded-md bg-card font-mono text-[11px] uppercase tracking-wider shadow-[2px_2px_0_0_hsl(var(--foreground))] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-transform"
      >
        <span className="flex items-center gap-2">
          <ListMusic className="w-3.5 h-3.5" strokeWidth={2.5} />
          <span>
            Up Next
            {hasItems && <span className="ml-1 text-muted-foreground">· {count}</span>}
            {!hasItems && loading && <span className="ml-1 text-muted-foreground">· …</span>}
          </span>
        </span>
        {open ? (
          <ChevronUp className="w-3.5 h-3.5" strokeWidth={2.5} />
        ) : (
          <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.5} />
        )}
      </button>

      {open && (
        <div id="up-next-list" className="mt-2">
          {!hasItems && loading && (
            <div className="font-mono text-xs text-muted-foreground py-3 px-1">Loading…</div>
          )}
          {!hasItems && !loading && (
            <div className="font-mono text-[11px] text-muted-foreground leading-relaxed px-1 py-2">
              Save episodes or follow a topic and they'll queue up automatically when this one ends.
            </div>
          )}
          {hasItems && <QueueList items={items} />}
        </div>
      )}
    </div>
  );
}

function QueueList({ items }: { items: QueueItem[] }) {
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    upNextStore.reorder(from, to);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="up-next">
        {(dropProvided) => (
          <ul
            ref={dropProvided.innerRef}
            {...dropProvided.droppableProps}
            className="flex flex-col gap-1.5 max-h-[440px] overflow-y-auto pr-1 -mr-1" style={{ touchAction: "pan-y" }}
          >
            {items.map((it, index) => (
              <Draggable key={it.episode_id} draggableId={it.episode_id} index={index}>
                {(dragProvided, snapshot) => (
                  <li
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    className={`flex items-center gap-2 p-2 border-2 border-foreground rounded-md bg-card shadow-[2px_2px_0_0_hsl(var(--foreground))] ${
                      snapshot.isDragging ? "shadow-[4px_4px_0_0_hsl(var(--foreground))] rotate-[-1deg]" : ""
                    }`}
                  >
                    <button
                      type="button"
                      {...dragProvided.dragHandleProps}
                      aria-label={`Drag to reorder ${it.title}`}
                      title="Drag to reorder"
                      className="flex-shrink-0 p-0.5 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
                    >
                      <GripVertical className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </button>

                    <QueueRow item={it} />
                  </li>
                )}
              </Draggable>
            ))}
            {dropProvided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  );
}

function QueueRow({ item: it }: { item: QueueItem }) {
  const artwork = it.podcast_image_url;
  return (
    <>
      <div
        className="flex-shrink-0 w-7 h-7 border-2 border-foreground rounded-md overflow-hidden bg-muted shadow-[1px_1px_0_0_hsl(var(--foreground))]"
        aria-hidden="true"
      >
        {artwork ? (
          <img
            src={artwork}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-accent/30" />
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          if (!it.audio_url) return;
          playerStore.playEpisode({
            key: it.episode_id,
            episodeId: it.episode_id,
            src: it.audio_url,
            title: it.title,
            podcastName: it.podcast_name ?? undefined,
            artworkUrl: it.podcast_image_url ?? undefined,
            episodeSlug: it.slug,
          });
          upNextStore.remove(it.episode_id);
        }}
        aria-label={`Play ${it.title}`}
        title={`Play ${it.title}`}
        className="flex-shrink-0 w-7 h-7 border-2 border-foreground rounded-md bg-[hsl(195,85%,55%)] text-white flex items-center justify-center shadow-[1px_1px_0_0_hsl(var(--foreground))] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-transform"
      >
        <Play className="w-3 h-3 ml-[1px]" strokeWidth={3} fill="currentColor" />
      </button>

      <div className="min-w-0 flex-1">
        <div className="font-mono font-bold text-[11px] truncate leading-tight">
          {it.title}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
          <span className="font-mono text-[9px] uppercase tracking-wider bg-accent/20 border border-foreground/40 rounded px-1 py-px leading-none flex-shrink-0">
            {it.source_label}
          </span>
          {it.podcast_name && (
            <span className="font-mono text-[10px] text-muted-foreground truncate">
              {it.podcast_name}
            </span>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => upNextStore.remove(it.episode_id)}
        aria-label={`Remove ${it.title} from queue`}
        title="Remove from queue"
        className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
    </>
  );
}
